import { assert } from '@ember/debug';
import { action } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import type {
  AppliedPropertyFilter,
  PropertyFilterInfo,
} from 'donor-elf-web/lib/filter-utils-new';

interface ComponentArgs {
  availablePropertyFilters: PropertyFilterInfo[];
  appliedPropertyFilters?: AppliedPropertyFilter[];
  showFilteringMessage?: boolean;
  initiallyOpened?: boolean;
  onFiltersChanged: (
    currentAppliedPropertyFilters: AppliedPropertyFilter[],
  ) => void;
}

class AppliedPropertyFilterEditingItem {
  constructor(args: { filter?: AppliedPropertyFilter; isNew?: boolean }) {
    this.appliedPropertyFilter = args.filter;
    this.isNew = args.isNew ?? false;
    this.editing = this.isNew;
  }

  @tracked editing = false;
  isNew = false;
  appliedPropertyFilter?: AppliedPropertyFilter;
}

export default class FilterComponent extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);
    assert(
      '@availablePropertyFilters cannot have duplicate .propertyName values',
      this.availablePropertyFiltersHasUniquePropertyNames,
    );
    this.mapAppliedPropertyFiltersToFilterItems();
  }

  @tracked filterItems: AppliedPropertyFilterEditingItem[] = [];

  @action
  mapAppliedPropertyFiltersToFilterItems() {
    this.filterItems =
      this.args.appliedPropertyFilters?.map(
        (x) => new AppliedPropertyFilterEditingItem({ filter: x }),
      ) || [];
  }

  get someFiltersAreEditing() {
    return this.filterItems.some((f) => f.editing);
  }

  get filterLabel() {
    if (this.args.appliedPropertyFilters?.length || 0 > 0) {
      return `${this.args.appliedPropertyFilters!.length} Filter${this.args.appliedPropertyFilters!.length === 1 ? '' : 's'}`;
    } else {
      return 'Filter';
    }
  }

  @action
  handleShowNewFilter() {
    if (this.someFiltersAreEditing) {
      return; // don't do anything
    }
    this.filterItems = [
      ...this.filterItems,
      new AppliedPropertyFilterEditingItem({ isNew: true }),
    ];
  }

  @action
  handleDropdownOnOpen() {
    if (this.filterItems.length === 0) {
      // have to wait until afterRender so Form::Underline::Select correctly opens
      scheduleOnce('afterRender', this, this.handleShowNewFilter);
    }
  }

  @action
  handleDropdownOnClose() {
    if (this.someFiltersAreEditing) {
      // delete the new filter item
      this.filterItems = this.filterItems.filter((x) => !x.isNew);
    }
  }

  @action
  handleClickedInFilterItemsDiv(e: MouseEvent) {
    if ((e.target! as HTMLElement).dataset.filterItemsDiv === 'true') {
      if (this.filterItems.every((x) => !x.editing)) {
        // if the user clicks in the blank space of filter items div, then show a
        // new filter item if there are no filter items currently being edited
        this.handleShowNewFilter();
      }
    }
  }

  @action
  handleEditFilter(filter: AppliedPropertyFilterEditingItem) {
    if (this.someFiltersAreEditing) {
      // delete the new filter item
      this.filterItems = this.filterItems.filter((x) => !x.isNew);
    }
    // cancel any other filter items that are currently being edited
    // to prevent multiple filter items from being edited at the same time
    for (let filterItem of this.filterItems) {
      if (filterItem.editing && filterItem != filter) {
        filterItem.editing = false;
      }
    }
    filter.editing = true;
  }

  @action
  handleAddFilter(filter: AppliedPropertyFilter) {
    this.args.onFiltersChanged([
      ...(this.args.appliedPropertyFilters || []),
      filter,
    ]);
  }

  @action
  handleChangeFilter(
    filterItem: AppliedPropertyFilterEditingItem,
    filter: AppliedPropertyFilter,
  ) {
    filterItem.appliedPropertyFilter = filter;
    this.args.onFiltersChanged(
      this.filterItems!.filter((x) => x.appliedPropertyFilter).map(
        (x) => x.appliedPropertyFilter!,
      ),
    );
  }

  @action
  handleDeleteFilter(
    filterItem: AppliedPropertyFilterEditingItem,
    _filter: AppliedPropertyFilter,
  ) {
    this.args.onFiltersChanged(
      this.filterItems!.filter((x) => x !== filterItem).map(
        (x) => x.appliedPropertyFilter!,
      ),
    );
  }

  @action
  handleFilterCancelled(filterItem: AppliedPropertyFilterEditingItem) {
    if (filterItem.isNew) {
      this.filterItems = this.filterItems.filter((x) => x !== filterItem);
    } else {
      filterItem.editing = false;
    }
  }

  @action
  handleClearFilters(dropdown: any) {
    this.args.onFiltersChanged([]);
    dropdown.actions.close();
  }

  @cached
  get availablePropertyFiltersByPropertyName(): {
    [propertyName: string]: PropertyFilterInfo;
  } {
    let byName: { [propertyName: string]: PropertyFilterInfo } = {};
    for (let info of this.args.availablePropertyFilters) {
      byName[info.propertyName] = info;
    }
    return byName;
  }

  get availablePropertyFiltersHasUniquePropertyNames() {
    let propertyNames: any = {};
    for (let x of this.args.availablePropertyFilters) {
      if (propertyNames[x.propertyName]) {
        return false;
      }
      propertyNames[x.propertyName] = true;
    }
    return true;
  }
}
